import { useMemo, useState } from 'react'
import { postDataAPI } from '../../../lib/api'
import OnboardingShell from './OnboardingShell'
import type { SignupUser } from '../SignupPage'
import Card from '../../../components/ui/Card'
import { OnboardingStep } from './steps'

export default function Step07TeamJoin({
  setStep,
  user,
  setUser,
}: {
  setStep: (n: number) => void
  user: SignupUser & { availableArtists?: any[] }
  setUser: React.Dispatch<React.SetStateAction<SignupUser>>
}) {
  const [selected, setSelected] = useState<any | null>(null)
  const [isJoining, setIsJoining] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const invitedArtists = useMemo(
    () => (Array.isArray((user as any).availableArtists) ? (user as any).availableArtists : []),
    [user]
  )

  const role = user.professionRole === 'manager' || user.professionRole === 'support' ? user.professionRole : 'support'
  const roleLabel = role === 'manager' ? 'Manager' : 'Support'

  const joinAndContinue = async () => {
    if (!user.email) return
    if (!selected) return
    setError(null)
    setIsJoining(true)
    try {
      const joinRes = await postDataAPI('team/join-artist', {
        teamMemberEmail: user.email,
        artistIds: [selected._id || selected.id].filter(Boolean),
        teamMemberPlan: (user as any).teamMemberPlan || (role === 'manager' ? 'basic' : 'support'),
      })
      if (joinRes?.status !== 200) throw new Error(joinRes?.message || 'Failed to join team')

      setUser((prev) => ({ ...prev, selectedArtists: [selected] } as any))
      setStep(OnboardingStep.TeamUsername)
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to join team')
    } finally {
      setIsJoining(false)
    }
  }

  return (
    <OnboardingShell
      title={`You’ve been invited as a ${roleLabel}`}
      subtitle="Choose the artist you were invited to support."
      footer="If you don’t see the artist here, ask them to invite this email from Team Management."
    >
      <div className="space-y-4">
        {invitedArtists.length ? (
          <div className="max-h-[45vh] overflow-y-auto space-y-2 pr-1">
            {invitedArtists.map((a: any) => {
              const key = a._id || a.id || a.email || a.name
              const active = selected?._id === a._id
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setSelected(active ? null : a)}
                  className="w-full text-left"
                >
                  <Card
                    className={`transition-all relative ${
                      active
                        ? 'border-gray-900 dark:border-white bg-gray-50 dark:bg-gray-700/50'
                        : 'hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {a.name || a.username || a.email}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">{a.email || ''}</div>
                      </div>
                      {active && (
                        <div className="flex-shrink-0">
                          <svg
                            className="w-5 h-5 text-gray-900 dark:text-white"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                      )}
                    </div>
                  </Card>
                </button>
              )
            })}
          </div>
        ) : (
          <div className="text-sm text-gray-500">No invitations found for this email.</div>
        )}

        {error ? <div className="text-red-600 text-sm">{error}</div> : null}

        <div className="flex gap-4 pt-2">
          <button type="button" onClick={() => setStep(OnboardingStep.VerifyOtp)} className="ds-btn ds-btn-secondary flex-1">
            Go back
          </button>
          <button
            type="button"
            disabled={!selected || isJoining || invitedArtists.length === 0}
            onClick={joinAndContinue}
            className="ds-btn ds-btn-primary flex-1"
          >
            {isJoining ? 'Joining…' : 'Continue'}
          </button>
        </div>
      </div>
    </OnboardingShell>
  )
}

